import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to track resource views and determine when to show newsletter prompt
 * Shows prompt after user has viewed 2-3 resources (not on first view)
 */
const useNewsletterPrompt = (resourceId) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const VIEWS_THRESHOLD = 2; // Show after viewing 2+ resources

  useEffect(() => {
    // Don't show if already subscribed
    if (localStorage.getItem('newsletter_subscribed') === 'true') {
      return;
    }

    // Don't show if user dismissed recently (within 24 hours)
    const dismissedAt = localStorage.getItem('newsletter_prompt_dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        return;
      }
    }

    // Track this resource view
    const viewedResources = JSON.parse(localStorage.getItem('viewed_resources') || '[]');
    
    if (!viewedResources.includes(resourceId)) {
      viewedResources.push(resourceId);
      localStorage.setItem('viewed_resources', JSON.stringify(viewedResources));
    }

    // Check if we should show the prompt
    // Show after viewing 2+ unique resources, with a small delay
    if (viewedResources.length >= VIEWS_THRESHOLD) {
      // Add a delay so it doesn't pop up immediately
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // 3 second delay

      return () => clearTimeout(timer);
    }
  }, [resourceId]);

  const dismissPrompt = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem('newsletter_prompt_dismissed', Date.now().toString());
  }, []);

  const hidePromptPermanently = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem('newsletter_subscribed', 'true');
  }, []);

  return {
    showPrompt,
    dismissPrompt,
    hidePromptPermanently
  };
};

export default useNewsletterPrompt;
